import { Color as ApiColor } from "../api/model";
import ColorSection from "../api/model/colors/ColorSection";
import Color from "../model/color";

import { colorEquals } from "./layer/color";

type TreeNode = {
    id?: string;
    name: string;
    path: string;
    children: TreeNode[];
    colorData: Color | null;
};

type Level = {
    [key: string]: Level;
} & {
    result: TreeNode[];
};

enum Marker {
    Yellow = "yellow",
    LightOrange = "lightOrange",
    Orange = "orange",
    Peach = "peach",
    Blue = "blue",
    Green = "green",
    Turquoise = "turquoise",
    DeepPurple = "deepPurple",
    Jade = "jade",
    Teflon = "teflon",
    Gandalf = "gandalf",
    Silverfish = "silverfish",
    EditorPink = "editorPink"
}

const API_COLOR_BY_MARKER: Record<Marker, ApiColor> = {
    [Marker.Yellow]: { r: 254, g: 207, b: 51, a: 1 },
    [Marker.LightOrange]: { r: 253, g: 189, b: 57, a: 1 },
    [Marker.Orange]: { r: 246, g: 152, b: 51, a: 1 },
    [Marker.Peach]: { r: 238, g: 103, b: 35, a: 1 },
    [Marker.Blue]: { r: 65, g: 155, b: 249, a: 1 },
    [Marker.Green]: { r: 200, g: 207, b: 45, a: 1 },
    [Marker.Turquoise]: { r: 13, g: 207, b: 218, a: 1 },
    [Marker.DeepPurple]: { r: 219, g: 124, b: 238, a: 1 },
    [Marker.Jade]: { r: 36, g: 179, b: 107, a: 1 },
    [Marker.Teflon]: { r: 85, g: 77, b: 86, a: 1 },
    [Marker.Gandalf]: { r: 151, g: 145, b: 151, a: 1 },
    [Marker.Silverfish]: { r: 225, g: 224, b: 225, a: 1 },
    [Marker.EditorPink]: { r: 255, g: 117, b: 135, a: 1 }
};

const CSS_VARIABLE_BY_MARKER: Record<Marker, string> = {
    [Marker.Yellow]: "--marker-yellow",
    [Marker.LightOrange]: "--marker-light-orange",
    [Marker.Orange]: "--marker-orange",
    [Marker.Peach]: "--marker-peach",
    [Marker.Blue]: "--marker-blue",
    [Marker.Green]: "--marker-green",
    [Marker.Turquoise]: "--marker-turquoise",
    [Marker.DeepPurple]: "--marker-deep-purple",
    [Marker.Jade]: "--marker-jade",
    [Marker.Teflon]: "--marker-teflon",
    [Marker.Gandalf]: "--marker-gandalf",
    [Marker.Silverfish]: "--marker-silverfish",
    [Marker.EditorPink]: "--marker-editor-pink"
};

/**
 * Returns actual name when the name has entered with slashes. Result is c in an example of a/b/c.
 * @param {string} fullname.
 * @returns {string} actual color name.
 */
function getActualName(fullname: string) {
    const formattedColorName = simplifyColorName(fullname);

    return formattedColorName.split("/").slice(-1).toString().trim();
}

/**
 * Returns section name of that color belongs to. Result is a/b in an example of a/b/c.
 * @param {string} fullname.
 * @returns {string} color section name.
 */
function getColorSectionName(fullname: string) {
    return fullname.split("/").slice(0, -1).join("/");
}

/**
 * Generates color sections from the given colors array.
 * @param {colors} colors.
 * @param {string} styleguideId.
 * @returns {ColorSection[]} color sections data.
 */
// TODO: [Revamp Color] Add unit tests for section calculations
function generateColorSectionsData(colors: Color[], styleguideId: string) {
    const tree = generateColorSectionsTree(styleguideId, colors);
    const defaultColors = tree.filter(node => node.colorData).map(item => item.colorData!);
    const defaultSection = ({
        id: `${styleguideId}`,
        colors: defaultColors
    });
    // Set default section as first section.
    const colorSections = [defaultSection];

    calculateSectionsData(tree, colorSections, styleguideId);

    return colorSections;
}

/**
 * Returns generated color section id with given parameters.
 * @param {string} sectionName.
 * @param {string} styleguideId.
 * @returns {string} color section id.
 */
function generateColorSectionId(sectionName: string, styleguideId: string) {
    return `${styleguideId}-${sectionName}`;
}

/**
 * Returns if the given node is a section.
 * @param {TreeNode} node.
 * @returns {boolean} if the node has any color data.
 */
function isSection(node: TreeNode) {
    return !node.colorData;
}

/**
 * Returns if the given node has a subsection.
 * @param {TreeNode} node.
 * @returns {boolean} if the node has any children that has any children.
 */
function hasSubSection(node: TreeNode) {
    return node.children.some(child => child.children.length > 0);
}

/**
 * Calculates color sections data recursively with the given tree.
 * @param {TreeNode} tree.
 * @param {ColorSection[]} colorSections.
 * @param {string} styleguideId.
 */
function calculateSectionsData(tree: TreeNode[], colorSections: ColorSection[], styleguideId: string) {
    tree.forEach(node => {
        if (isSection(node)) {
            const nodeColors = node.children.filter(item => item.colorData !== null);
            colorSections.push({
                id: generateColorSectionId(node.path, styleguideId),
                name: node.path,
                colors: nodeColors.length > 0 ? nodeColors.map(item => item.colorData!) : []
            });

            if (hasSubSection(node)) {
                calculateSectionsData(node.children, colorSections, styleguideId);
            }
        }
    });
}

/**
 * Returns tree of the colors array in the given order.
 * @param {string} styleguideId
 * @param {colors} colors array to separate into sections.
 * @returns {TreeNode[]} tree.
 */
function generateColorSectionsTree(styleguideId: string, colors: Color[]) {
    const result: TreeNode[] = [];
    const level = { result } as Level;

    colors.forEach(color => {
        const colorName = simplifyColorName(color.name!);
        const pathArray = colorName.split("/");

        pathArray.reduce((acc, name, currentIndex) => {
            const isColor = currentIndex === pathArray.length - 1;
            const path = pathArray.slice(0, currentIndex + 1).join("/");
            const id = isColor ? color.id! : generateColorSectionId(path, styleguideId);

            if (!acc[id]) {
                acc[id] = {
                    result: [] as TreeNode[]
                } as Level;

                acc.result.push({
                    id,
                    name,
                    path,
                    colorData: isColor ? color : null,
                    children: acc[id].result
                });
            }

            return acc[id];
        }, level);
    });

    return result;
}

/**
 * Returns color names only with one slash in middle.
 * @param {string} name.
 * @param {string} name.
 */
function simplifyColorName(name: string) {
    // Remove first and last slashes, and then the middle ones if they are more than two.
    return name.replace(/^\/+|\/+$/g, "").replace(/\/{2,}/g, "/");
}

/**
 * Returns subsections belongs to given section.
 * @param {ColorSection[]} sections.
 * @param {ColorSection} section.
 */
function getSubsections(sections: ColorSection[], section: ColorSection) {
    if (!section.name) {
        return null;
    }

    return sections.filter(item => item.name?.startsWith(`${section.name!}/`));
}

/**
 * Maps a color returned from the API to a marker color.
 * Use `getMarkerCSSVariableForApiColor` instead if you need to map to a CSS variable directly.
 * @param apiColor Color model returned from API
 * @returns Marker color
 */
function getMarkerForApiColor(apiColor: ApiColor) {
    const allMarkers = Object.keys(API_COLOR_BY_MARKER) as Marker[];

    const marker = allMarkers.find(key => colorEquals(API_COLOR_BY_MARKER[key], apiColor));
    if (!marker) {
        return;
    }

    // With the redesign, green is replaced with jade.
    if (marker === Marker.Green) {
        return Marker.Jade;
    }

    return marker;
}

/**
 * Maps a marker color to a color returned from the API.
 * @param marker Marker color
 * @returns Color returned from the API
 */
function getApiColorForMarker(marker: Marker) {
    return API_COLOR_BY_MARKER[marker];
}

/**
 * Maps colors returned from the API to a CSS color variable.
 * @param apiColor Color model returned from API
 * @param options Options, defaults to including `var()` function
 * @returns CSS color variable
 */
function getMarkerCSSVariableForApiColor(
    apiColor: ApiColor,
    options: { includeVarFunction?: boolean; } = { includeVarFunction: true }
): string | undefined {
    const markerColor = getMarkerForApiColor(apiColor);

    if (!markerColor) {
        return;
    }

    const variable = CSS_VARIABLE_BY_MARKER[markerColor];
    if (!options.includeVarFunction) {
        return variable;
    }

    return `var(${variable})`;
}

export {
    Marker,
    getActualName,
    getColorSectionName,
    generateColorSectionId,
    generateColorSectionsData,
    getSubsections,
    getMarkerForApiColor,
    getApiColorForMarker,
    getMarkerCSSVariableForApiColor
};
